import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, ArrowLeft, ArrowRight, Check, Sparkles, Zap, Crown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../ThemeToggle';
import logo from '../../assets/logo.svg';
import EmailVerification from './EmailVerification';
import PasswordStrength, { scorePassword } from './PasswordStrength';

interface RegistrationData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  company: string;
  selectedPlan: string;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  documents_limit: number;
  ai_queries_limit: number;
  claude_model: string;
  features: string[];
}

const MultiStepRegistration = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerification, setShowVerification] = useState(false);

  const [registrationData, setRegistrationData] = useState<RegistrationData>({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    company: '',
    selectedPlan: 'discovery'
  });

  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Plan definitions matching the backend
  const plans: Plan[] = [
    {
      id: 'discovery',
      name: 'Discovery',
      description: '14-day free trial',
      price: 0,
      documents_limit: 10,
      ai_queries_limit: 10,
      claude_model: 'claude-sonnet-4-6',
      features: [
        '10 documents total',
        '10 AI queries',
        '2 invoice generations',
        '3 supplier templates',
        'Advanced document analysis',
        'Expense tracking',
        '14-day trial period'
      ]
    },
    {
      id: 'essential',
      name: 'Essential',
      description: 'Perfect for basic invoice analysis',
      price: 9,
      documents_limit: 20,
      ai_queries_limit: 50,
      claude_model: 'claude-haiku-4-5',
      features: [
        '20 documents/month',
        '50 AI queries/month with basic AI model',
        '2 invoice creations/month',
        '2 PDF downloads/month',
        '3 supplier templates',
        'Fast & efficient processing',
        'Email support',
        'Monthly reset'
      ]
    },
    {
      id: 'advanced',
      name: 'Advanced',
      description: 'For advanced analysis with powerful AI assistant',
      price: 19,
      documents_limit: 75,
      ai_queries_limit: 100,
      claude_model: 'claude-sonnet-4-6',
      features: [
        '75 documents/month',
        '100 AI queries/month with advanced AI model',
        '3 invoice creations/month',
        '5 invoice edits/month',
        '5 PDF downloads/month',
        '9 supplier templates',
        'Advanced document analysis',
        'Compliance checking',
        'Expense tracking',
        'KSeF e-invoicing (Poland)',
        'Priority support',
        'Monthly reset'
      ]
    }
  ];

  const updateRegistrationData = (updates: Partial<RegistrationData>) => {
    setRegistrationData(prev => ({ ...prev, ...updates }));
  };

  const validateStep1 = () => {
    const { username, email, password, confirmPassword } = registrationData;

    if (!username || !email || !password) {
      setError('Please fill in all required fields');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    const strength = scorePassword(password, email, username);
    if (!strength.acceptable) {
      setError(strength.hint || 'Please choose a stronger password (at least 8 characters)');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    setError('');

    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await register(
        registrationData.username,
        registrationData.email,
        registrationData.password,
        registrationData.fullName || undefined,
        registrationData.company || undefined,
        registrationData.selectedPlan
      );

      // Check if verification is required
      if (result && result.requires_verification) {
        // Show verification screen
        setShowVerification(true);
      } else {
        // Old flow - direct login (shouldn't happen with new backend)
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'discovery':
        return <Sparkles className="w-6 h-6" />;
      case 'essential':
        return <Zap className="w-6 h-6" />;
      case 'advanced':
        return <Crown className="w-6 h-6" />;
      default:
        return <Sparkles className="w-6 h-6" />;
    }
  };

  const getModelBadge = (model: string) => {
    switch (model) {
      case 'claude-3-haiku-20240307':       // legacy
      case 'claude-3-5-haiku-20241022':     // legacy
      case 'claude-haiku-4-5':              // current basic tier
        return { text: 'Basic AI', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' };
      case 'claude-sonnet-4-20250514':      // legacy
      case 'claude-sonnet-4-6':             // current advanced tier
      case 'claude-opus-4-8':               // current premium tier
        return { text: 'Advanced AI', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' };
      default:
        return { text: 'AI Powered', color: 'bg-gray-500/20 text-gray-300 border-gray-500/30' };
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Create Your Account</h2>
        <p className="text-foreground-muted text-sm">Let's get started with your basic information</p>
      </div>

      <div className="space-y-2">
        <input
          type="text"
          placeholder="Username"
          value={registrationData.username}
          onChange={(e) => updateRegistrationData({ username: e.target.value })}
          className="w-full p-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
          required
        />
      </div>

      <div className="space-y-2">
        <input
          type="email"
          placeholder="Email"
          value={registrationData.email}
          onChange={(e) => updateRegistrationData({ email: e.target.value })}
          className="w-full p-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
          required
        />
      </div>

      <div className="space-y-2">
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={registrationData.password}
            onChange={(e) => updateRegistrationData({ password: e.target.value })}
            className="w-full p-2 pr-10 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-coral-400 hover:text-foreground transition-colors"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <PasswordStrength
          password={registrationData.password}
          email={registrationData.email}
          username={registrationData.username}
        />
      </div>

      <div className="space-y-2">
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={registrationData.confirmPassword}
            onChange={(e) => updateRegistrationData({ confirmPassword: e.target.value })}
            className="w-full p-2 pr-10 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-coral-400 hover:text-foreground transition-colors"
          >
            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <input
          type="text"
          placeholder="Full Name (optional)"
          value={registrationData.fullName}
          onChange={(e) => updateRegistrationData({ fullName: e.target.value })}
          className="w-full p-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
        />
      </div>

      <div className="space-y-2">
        <input
          type="text"
          placeholder="Company (optional)"
          value={registrationData.company}
          onChange={(e) => updateRegistrationData({ company: e.target.value })}
          className="w-full p-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Choose Your Plan</h2>
        <p className="text-foreground-muted text-sm">Select the plan that best fits your needs</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const modelBadge = getModelBadge(plan.claude_model);
          const isSelected = registrationData.selectedPlan === plan.id;
          const isPopular = plan.id === 'advanced';

          return (
            <div
              key={plan.id}
              className={`relative cursor-pointer transition-all ${
                isSelected
                  ? 'ring-2 ring-coral-400 bg-coral-500/20'
                  : 'hover:bg-coral-500/10'
              } ${
                isPopular ? 'border-coral-400' : 'border-coral-500/20'
              } bg-black/30 backdrop-blur-sm rounded-xl p-4 border`}
              onClick={() => updateRegistrationData({ selectedPlan: plan.id })}
            >
              {isPopular && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-coral-500 text-white text-xs font-semibold rounded-full">
                  POPULAR
                </div>
              )}

              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-coral-500/20 rounded-lg mb-3">
                  <div className="text-coral-400">{getPlanIcon(plan.id)}</div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">
                  {plan.name}
                </h3>
                <p className="text-xs text-foreground-muted mb-3">{plan.description}</p>
                <div className="mb-2">
                  <span className="text-2xl font-bold text-foreground-muted">
                    €{plan.price}
                  </span>
                  <span className="text-foreground-muted text-xs ml-1">
                    {plan.price === 0 ? '14 days' : '/month'}
                  </span>
                </div>
                {plan.id !== 'discovery' && (
                  <div className={`inline-flex items-center px-2 py-1 border rounded-full ${modelBadge.color}`}>
                    <span className="text-xs">{modelBadge.text}</span>
                  </div>
                )}
              </div>

              <ul className="space-y-2 mb-4">
                {plan.features.slice(0, 3).map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-3 h-3 text-green-400 shrink-0 mt-1" />
                    <span className="text-xs text-foreground-secondary">{feature}</span>
                  </li>
                ))}
              </ul>

              {isSelected && (
                <div className="absolute top-2 right-2">
                  <div className="w-6 h-6 bg-coral-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStep3 = () => {
    const selectedPlan = plans.find(p => p.id === registrationData.selectedPlan);

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Review & Confirm</h2>
          <p className="text-foreground-muted text-sm">Confirm your account details and plan selection</p>
        </div>

        <div className="bg-coral-500/10 border border-coral-500/20 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-3">Account Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-foreground-muted">Username:</span>
              <span className="text-white">{registrationData.username}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-muted">Email:</span>
              <span className="text-white">{registrationData.email}</span>
            </div>
            {registrationData.fullName && (
              <div className="flex justify-between">
                <span className="text-foreground-muted">Name:</span>
                <span className="text-white">{registrationData.fullName}</span>
              </div>
            )}
            {registrationData.company && (
              <div className="flex justify-between">
                <span className="text-foreground-muted">Company:</span>
                <span className="text-white">{registrationData.company}</span>
              </div>
            )}
          </div>
        </div>

        {selectedPlan && (
          <div className="bg-coral-500/10 border border-coral-500/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-3">Selected Plan</h3>
            <div className="flex items-center gap-3 mb-3">
              <div className="text-coral-400">{getPlanIcon(selectedPlan.id)}</div>
              <div>
                <h4 className="text-white font-semibold">{selectedPlan.name}</h4>
                <p className="text-foreground-muted text-sm">€{selectedPlan.price}{selectedPlan.price === 0 ? ' (14-day trial)' : '/month'}</p>
              </div>
            </div>
            <ul className="space-y-1">
              {selectedPlan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Check className="w-3 h-3 text-green-400 shrink-0 mt-1" />
                  <span className="text-xs text-foreground-secondary">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* This promised an immediate redirect to checkout. handleSubmit only
            calls register(), and AuthContext handles checkout_url solely in the
            legacy access_token branch that the requires_verification flow
            returns before ever reaching — so no checkout was ever opened. */}
        {registrationData.selectedPlan !== 'discovery' && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <p className="text-yellow-300 text-sm">
              <strong>Note:</strong> Verify your email first. You can then start your{' '}
              {registrationData.selectedPlan} subscription from the Pricing page once you're
              signed in — you won't be charged before that.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Show verification screen if registration successful
  if (showVerification) {
    return <EmailVerification email={registrationData.email} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-white via-coral-50 to-white dark:from-background dark:via-background dark:to-background">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-2xl bg-black/30 backdrop-blur-sm rounded-lg p-6 border border-coral-500/20">
        <div className="text-center mb-6">
          <img
            src={logo}
            alt="ViewPoint Logo"
            className="w-12 h-12 mx-auto mb-4"
          />

          {/* Progress indicator */}
          <div className="flex justify-center mb-6">
            <div className="flex items-center space-x-2">
              {[1, 2, 3].map((step) => (
                <React.Fragment key={step}>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      step <= currentStep
                        ? 'bg-coral-500 text-white'
                        : 'bg-coral-500/20 text-coral-400'
                    }`}
                  >
                    {step < currentStep ? <Check className="w-4 h-4" /> : step}
                  </div>
                  {step < 3 && (
                    <div
                      className={`w-8 h-0.5 ${
                        step < currentStep ? 'bg-coral-500' : 'bg-coral-500/20'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-200 mb-6">
            <AlertCircle className="w-4 h-4" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}

        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={currentStep === 1 ? () => navigate('/login') : handlePrevStep}
            className="flex items-center gap-2 px-4 py-2 text-foreground-muted hover:text-foreground-secondary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {currentStep === 1 ? 'Back to Login' : 'Previous'}
          </button>

          {currentStep < 3 ? (
            <button
              type="button"
              onClick={handleNextStep}
              className="flex items-center gap-2 px-6 py-2 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiStepRegistration;